import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { useNavigate, useLocation } from "react-router-dom";

import { drawerWidth, images, routes, sideDrawerListItems, orgAdminSidebarSections } from "../constants";
import { useAuth } from "../hooks";
import { useOrganizationContext } from "../providers/OrganizationAdmin/OrganizationProvider";
import UserProfileImage from "../GeneralAdmin/modules/GAUserProfile/components/UserProfileImage/UserProfileImage";
import { useNotificationBadgeContext } from "../providers/NotificationBadgeProvider";
import NotificationBadge from "./NotificationBadge";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    onLogout,
    currentUser,
    organizationId,
    currentLayoutAccess,
    setOrganizationId,
    setCurrentLayoutAccess,
    navigationRoute,
  } = useAuth();
  const { currentOrganization, handleGetOrganization } =
    useOrganizationContext();
  const [sideDrawerItems, setSideDrawerItems] = useState([
    ...sideDrawerListItems,
  ]);
  const [currentOrgData, setCurrentOrgData] = useState(null);
  const { notificationCount, markNotificationsAsViewed } = useNotificationBadgeContext();
  const adminType = sessionStorage.getItem("adminType");

  // Collapsible sidebar state - tracks which sections are open
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    handleGetOrganization();
  }, []);
  useEffect(() => {
    setCurrentOrgData(currentOrganization);
  }, [currentOrganization]);

  // Auto-expand the section that contains the current route
  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenSections = {};

    orgAdminSidebarSections.forEach((section) => {
      if (section.collapsible && section.children) {
        const hasActiveChild = section.children.some((child) => {
          // Check if current path starts with child path (for nested routes)
          return currentPath === child.path || currentPath.startsWith(child.path + "/");
        });
        if (hasActiveChild) {
          newOpenSections[section.title] = true;
        }
      }
    });

    // Auto-expand the General Admin section when on the Metrics Dashboard route
    if (
      currentPath === routes.organizationDashboard ||
      currentPath.startsWith(routes.organizationDashboard + "/")
    ) {
      newOpenSections["General Admin"] = true;
    }

    // Only update if we found a matching section (don't close everything on unrelated pages)
    if (Object.keys(newOpenSections).length > 0) {
      setOpenSections((prev) => ({ ...prev, ...newOpenSections }));
    }
  }, [location.pathname]);

  // Legacy selected state for flat sidebar items (kept for backward compat)
  useEffect(() => {
    let temp = [...sideDrawerItems];
    let paths = window.location.pathname.split("/");
    let newPathname = "/";
    newPathname += paths[1];

    temp.forEach((item) => {
      let index = temp.indexOf(item);
      item.selected = false;
      if (item.path == window.location.pathname) {
        temp[index].selected = true;
      }
      if (item.path == newPathname) {
        temp[index].selected = true;
      }
    });
    setSideDrawerItems(temp);
  }, [window.location.pathname]);

  // Toggle a collapsible section open/closed (accordion: only one open at a time)
  const handleToggleSection = (sectionTitle) => {
    setOpenSections((prev) => {
      const isCurrentlyOpen = prev[sectionTitle];
      // Close all sections, then toggle the clicked one
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
      newState[sectionTitle] = !isCurrentlyOpen;
      return newState;
    });
  };

  // Check if a child item is the currently active/selected route
  const isChildActive = (childPath) => {
    const currentPath = location.pathname;
    return currentPath === childPath || currentPath.startsWith(childPath + "/");
  };

  // Handle clicking a child navigation item
  const handleChildClick = (child) => {
    if (child.openImportModal) {
      navigate(child.path, { state: { openImportModal: true } });
    } else {
      navigate(child.path);
    }
  };

  // Determine if the sidebar is in org admin mode
  const isOrgAdmin =
    currentUser?.type === "organization" || currentLayoutAccess === "organization";

  // Render the collapsible org admin sidebar sections
  const renderOrgAdminSidebar = () => {
    return orgAdminSidebarSections.map((section, sectionIndex) => {
      if (section.collapsible && section.children) {
        // Collapsible section with children
        const isOpen = openSections[section.title] || false;
        return (
          <React.Fragment key={section.title}>
            {/* Section Header */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleToggleSection(section.title)}
                style={{ padding: 0, margin: 0 }}
              >
                <ListItemText style={listItemStyle}>
                  <span style={sectionHeaderTextStyles}>
                    {section.title}{" "}
                    <span style={caretStyle}>
                      {isOpen ? "▾" : "▸"}
                    </span>
                  </span>
                </ListItemText>
              </ListItemButton>
            </ListItem>

            {/* Collapsible Children */}
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {section.children.map((child) => {
                  const active = isChildActive(child.path);
                  return (
                    <ListItem key={child.title} disablePadding>
                      <ListItemButton
                        onClick={() => handleChildClick(child)}
                        style={{ padding: 0, margin: 0 }}
                      >
                        <ListItemText
                          style={active ? childItemSelectedStyle : childItemStyle}
                        >
                          <span style={childItemTextStyles}>
                            {child.title}
                          </span>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </React.Fragment>
        );
      } else {
        // Standalone item (Notifications, Help, Logout)
        const active = section.path && isChildActive(section.path);
        return (
          <ListItem key={section.title} disablePadding>
            <ListItemButton
              onClick={
                section.title === "Logout"
                  ? () => onLogout()
                  : () => {
                      if (section.title === "Notifications") {
                        markNotificationsAsViewed();
                      }
                      navigate(section.path);
                    }
              }
              style={{ padding: 0, margin: 0 }}
            >
              <ListItemText
                style={active ? listItemSelectedStyle : listItemStyle}
              >
                {section.title === "Notifications" ? (
                  <NotificationBadge count={notificationCount}>
                    <span style={sectionHeaderTextStyles}>{section.title}</span>
                  </NotificationBadge>
                ) : (
                  <span style={sectionHeaderTextStyles}>{section.title}</span>
                )}
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      }
    });
  };

  return ( 
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          background: "#00A7CD",
          overflowX: 'hidden',
          overflowY: 'auto',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <Toolbar
        onClick={() => navigate(routes.home)}
        style={{ cursor: "pointer" }}
      >
        <img
          src={images.QuickTalesIcon}
          alt="quick tales icon"
          style={{ height: 30, width: 118 }}
        />
      </Toolbar>
      {currentUser?.type == "organization" ||
      currentLayoutAccess === "organization"
        ? currentOrgData &&
          currentOrgData?.logoOP && (
          <img
              src={
                currentOrgData && currentOrgData?.logoOP
                  ? currentOrgData?.logoOP
                  : images.LASHistoricParkIcon
              }
              alt="quick tales icon"
              onClick={() => navigate(routes.home)}
              style={{
                height: 80,
                width: drawerWidth - 1,
                objectFit: "contain",
                cursor: "pointer",
              }}
            />
          )
        : ""}
      <List style={{ height: "100%" }}>
        {isOrgAdmin ? (
          <>
            {renderOrgAdminSidebar()}
            {adminType === "general" && (
              <React.Fragment key="General Admin">
                {/* Section Header */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleToggleSection("General Admin")}
                    style={{ padding: 0, margin: 0 }}
                  >
                    <ListItemText style={listItemStyle}>
                      <span style={sectionHeaderTextStyles}>
                        General Admin{" "}
                        <span style={caretStyle}>
                          {openSections["General Admin"] ? "▾" : "▸"}
                        </span>
                      </span>
                    </ListItemText>
                  </ListItemButton>
                </ListItem>

                {/* Collapsible Children */}
                <Collapse
                  in={openSections["General Admin"] || false}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {/* Gen Admin */}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          localStorage.setItem("currentLayoutAccess", "general");
                          setOrganizationId(null);
                          setCurrentLayoutAccess("general");
                          navigate(navigationRoute, {
                            state: {
                              orgId: localStorage.getItem("organizationId"),
                            },
                          });
                        }}
                        style={{ padding: 0, margin: 0 }}
                      >
                        <ListItemText style={childItemStyle}>
                          <span style={childItemTextStyles}>Gen Admin</span>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>

                    {/* Metrics Dashboard */}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(routes.organizationDashboard);
                        }}
                        style={{ padding: 0, margin: 0 }}
                      >
                        <ListItemText
                          style={
                            isChildActive(routes.organizationDashboard)
                              ? childItemSelectedStyle
                              : childItemStyle
                          }
                        >
                          <span style={childItemTextStyles}>
                            Metrics Dashboard
                          </span>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Collapse>
              </React.Fragment>
            )}
          </>
        ) : (
          <>
            {sideDrawerItems.map((item, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton
                  onClick={
                    item.title === "Logout"
                      ? () => onLogout()
                      : () => {
                          if (item.title === "Notifications") {
                            markNotificationsAsViewed();
                          }
                          let temp = [...sideDrawerItems];
                          temp.forEach((item) => {
                            item.selected = false;
                          });
                          let index = temp.indexOf(item);
                          temp[index].selected = true;
                          setSideDrawerItems(temp);
                          navigate(item.path);
                        }
                  }
                  style={{ padding: 0, margin: 0 }}
                >
                  <ListItemText
                    style={item.selected ? listItemSelectedStyle : listItemStyle}
                  >
                    {item.title === "Logout" ? (
                      <span onClick={onLogout} style={listItemTextStyles}>
                        {item.title}
                      </span>
                    ) : item.title === "Notifications" ? (
                      <NotificationBadge count={notificationCount}>
                        <span style={listItemTextStyles}>{item.title}</span>
                      </NotificationBadge>
                    ) : (
                      <span style={listItemTextStyles}>{item.title}</span>
                    )}
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
            {adminType === "general" && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(routes.organizationDashboard);
                  }}
                  style={{ padding: 0, margin: 0 }}
                >
                  <ListItemText
                    style={
                      window.location.pathname === routes.organizationDashboard
                        ? listItemSelectedStyle
                        : listItemStyle
                    }
                  >
                    <span style={listItemTextStyles}>Metrics Dashboard</span>
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            )}
            {adminType === "general" && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    localStorage.setItem("currentLayoutAccess", "general");
                    setOrganizationId(null);
                    setCurrentLayoutAccess("general");
                    navigate(navigationRoute, {
                      state: {
                        orgId: localStorage.getItem("organizationId"),
                      },
                    });
                  }}
                  style={{ padding: 0, margin: 0 }}
                >
                  <ListItemText style={listItemStyle}>
                    <span style={listItemTextStyles}>General Admin</span>
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            )}
          </>
        )}
        {/* User Profile - inside the list so it scrolls with menu items */}
        <ListItem disablePadding style={{ marginTop: 16 }}>
          <UserProfileImage onClick={() => navigate(routes.userProfile)} />
        </ListItem>
      </List>
    </Drawer>
  );
};

export default Sidebar;

// Shared text styles
const listItemTextStyles = {
  color: "#FCFCFC",
  fontWeight: 700,
  fontSize: 14,
  fontFamily: "Avenir Book, sans-serif",
};

// Section header text (bold, for collapsible headers and standalone items)
const sectionHeaderTextStyles = {
  color: "#FCFCFC",
  fontWeight: 500,
  fontSize: 17,
  fontFamily: "Avenir Book, sans-serif",
};

// Caret indicator style
const caretStyle = {
  fontSize: 30,
  marginLeft: 2,
  marginTop: '-16px',
  display: 'inline-block',
  verticalAlign: 'middle',
  position: 'relative',
  top: '7px',   // adjust until it looks right, usually -1 to -3px
};

// Child item text (regular weight, for sub-items)
const childItemTextStyles = {
  color: "#FCFCFC",
  fontWeight: 400,
  fontSize: 15,
  fontFamily: "Avenir Book, sans-serif",
};

const listItemStyle = {
  padding: "6px 6px",
  fontFamily: "Avenir Book, sans-serif",
};

const listItemSelectedStyle = {
  padding: "12px 6px",
  background: "#1B5765",
  fontFamily: "Avenir Book, sans-serif",
};

// Child item style (indented)
const childItemStyle = {
  padding: "4px 6px 4px 20px",
  fontFamily: "Avenir Book, sans-serif",
};

// Child item selected style (indented + highlighted)
const childItemSelectedStyle = {
  padding: "4px 6px 4px 20px",
  background: "#1B5765",
  fontFamily: "Avenir Book, sans-serif",
};
